import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readFields } from "@/office/word/layout";

export const lektionQuerverweise: Lesson = {
  id: "querverweise",
  title: "Querverweise",
  summary: "„Siehe Kapitel 3“ – und es stimmt auch noch nach dem Umbau.",
  durationMinutes: 8,
  learningGoals: [
    "Querverweise auf Überschriften und Abbildungen einfügen",
    "Verweise nach Änderungen aktualisieren",
    "Den Unterschied zu getipptem Text erkennen",
  ],
  steps: [
    {
      id: "verweis-einfuegen",
      title: "Einen Querverweis einfügen",
      explanation: [
        "„Die Einzelheiten stehen in Kapitel 3“ – solange, bis jemand ein Kapitel " +
          "einfügt. Dann steht es in Kapitel 4, und im Text bleibt die falsche Zahl.",
        "Ein Querverweis löst das. Unter „Verweise“ → „Querverweis“ wählst du zuerst den " +
          "Verweistyp (Überschrift, Abbildung, Tabelle, Fußnote) und dann das konkrete " +
          "Ziel. Darunter legst du fest, was im Text erscheinen soll: nur die Nummer, nur " +
          "der Text der Überschrift, die Seitenzahl oder eine Kombination.",
        "Word fügt daraufhin ein Feld ein – dieselbe Technik wie bei Seitenzahlen und " +
          "Inhaltsverzeichnis. Der Haken „Als Hyperlink einfügen“ macht daraus zusätzlich " +
          "einen anklickbaren Sprung, der auch im PDF erhalten bleibt.",
        "Wie alle Felder aktualisieren sich Querverweise nicht von selbst: Strg + A, dann " +
          "F9.",
      ],
      keyPoints: [
        "Verweise → Querverweis, dann Typ und Ziel wählen.",
        "„Als Hyperlink einfügen“ erzeugt einen anklickbaren Sprung.",
        "Strg + A und F9 aktualisiert alle Verweise.",
      ],
      task: {
        instruction:
          "Füge in deinem Dokument einen Querverweis auf eine deiner Überschriften oder " +
          "Beschriftungen ein.",
        hint: "Voraussetzung ist mindestens eine Überschrift oder Beschriftung im Dokument.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) {
            return unsupported(
              "Felder sind dem Add-in erst ab WordApi 1.4 zugänglich – diese Word-Version " +
                "stellt sie nicht zur Verfügung.",
              "Prüfe selbst mit Alt + F9, ob im Text ein REF-Feld steht, und markiere den Schritt dann als erledigt.",
            );
          }

          // Querverweise sind REF-Felder; Word setzt bei Überschriften zusätzlich Lesezeichen.
          const querverweise = felder.filter(
            (feld) => /^\s*(REF|PAGEREF|NOTEREF)\b/i.test(feld.code) || feld.type === "Ref",
          );

          return fromCriteria(
            [
              criterion(
                `Mindestens ein Querverweis im Dokument (gefunden: ${querverweise.length})`,
                querverweise.length >= 1,
              ),
            ],
            {
              success: "Der Verweis ist ein Feld – er stimmt auch nach dem nächsten Umbau noch.",
              failure: "Es wurde noch kein Querverweis gefunden.",
              hint: "Getippter Text zählt nicht. Der Verweis muss über Verweise → Querverweis eingefügt werden.",
            },
          );
        },
      },
    },
    {
      id: "zitate",
      title: "Zitate und Literaturverzeichnis",
      explanation: [
        "Für wissenschaftliche Arbeiten hat Word eine eigene Quellenverwaltung. Unter " +
          "„Verweise“ → „Quellen verwalten“ legst du Bücher, Aufsätze und Webseiten " +
          "einmal an; über „Zitat einfügen“ setzt du sie im Text.",
        "Unter „Formatvorlage“ wählst du den Zitierstil – APA, Chicago, ISO 690 und " +
          "weitere. Ändert deine Hochschule die Vorgabe, stellst du den Stil um und alle " +
          "Zitate im Dokument folgen automatisch.",
        "Am Ende erzeugt „Literaturverzeichnis“ die vollständige Liste aller " +
          "verwendeten Quellen – wieder als Feld, das sich mit F9 aktualisiert.",
        "Für umfangreiche Arbeiten stoßen die Bordmittel allerdings an Grenzen. Wer mit " +
          "hunderten Quellen arbeitet, ist mit einem eigenen Literaturverwaltungsprogramm " +
          "besser bedient – die meisten bringen ein Word-Add-in mit, das sich ähnlich " +
          "bedienen lässt.",
      ],
      keyPoints: [
        "Verweise → Quellen verwalten, dann Zitat einfügen.",
        "Der Zitierstil lässt sich jederzeit für das ganze Dokument umstellen.",
        "Das Literaturverzeichnis ist ein Feld und wird mit F9 aktualisiert.",
      ],
      task: {
        instruction:
          "Lege über „Quellen verwalten“ mindestens zwei Quellen an, zitiere beide im Text " +
          "und füge am Ende ein Literaturverzeichnis ein.",
        hint: "Verweise → Zitat einfügen → Neue Quelle hinzufügen. Danach Verweise → Literaturverzeichnis.",
        check: async () => {
          const felder = await readFields();
          if (felder === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Felder zur Verfügung (ab WordApi 1.4).",
              "Mit Alt + F9 zeigen sich Zitate als { CITATION … }. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          const zitate = felder.filter((feld) => /^CITATION\b/i.test(feld.code));
          const quellen = new Set(zitate.map((feld) => /^CITATION\s+(\S+)/i.exec(feld.code)?.[1] ?? ""));
          const verzeichnis = felder.some((feld) => /^BIBLIOGRAPHY\b/i.test(feld.code));
          return fromCriteria(
            [
              criterion(`Mindestens zwei verschiedene Quellen zitiert (gefunden: ${quellen.size})`, quellen.size >= 2),
              criterion("Ein Literaturverzeichnis ist eingefügt", verzeichnis),
            ],
            {
              success: "Zitate und Verzeichnis hängen zusammen – ein Stilwechsel ändert alles auf einmal.",
              failure: "Zitate oder Literaturverzeichnis fehlen noch.",
              hint: "Getippte Klammern wie „(Meier 2024)“ sind keine Zitate – nutze Verweise → Zitat einfügen.",
            },
          );
        },
      },
    },
  ],
};
